import type { RowSelectionState, Table } from "@tanstack/react-table";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { DownloadIcon, Trash2Icon, Undo2Icon, XIcon } from "lucide-react";
import { useState } from "react";
import { Button as RACButton } from "react-aria-components";

import { Button } from "@/components/button";
import { UnlockModal } from "@/components/upgrade-modals/unlock-modal";
import { FeatureGuard } from "@/features/auth/permissions/feature-guard";
import { useIsMobileScreen } from "@/hooks/useIsMobileScreen";

import type { FileItem, View } from "../types";

import { useDownloadBulkFiles } from "../hooks/useDownloadBulkFiles";
import { useRestoreFilesMutation } from "../hooks/useRestoreFilesMutation";
import { useDeleteFilesDialogState } from "./file-delete-dialog";
import { useRestoreFilesDialogState } from "./file-restored-dialog";
import { UploadAction } from "./upload-action";

type SelectionActionsProps = {
  isTemplate?: boolean;
  rowSelection: RowSelectionState;
  table: Table<FileItem>;
  view: View;
};

export function SelectionActions({
  isTemplate,
  rowSelection,
  table,
  view,
}: SelectionActionsProps) {
  const isMobile = useIsMobileScreen();
  const { folder } = useSearch({ strict: false });
  const { toggle: toggleRestoreFilesDialog } = useRestoreFilesDialogState();
  const { setSelectedItemsIds, toggle: toggleDeleteFilesDialog } =
    useDeleteFilesDialogState();
  const { mutate: downloadBulkFiles } = useDownloadBulkFiles();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const navigate = useNavigate();
  const restoreFromTrashMutation = useRestoreFilesMutation({
    onSuccess: () => {
      table.resetRowSelection();
      toggleRestoreFilesDialog();
    },
  });

  const isTrashFolder = folder === "trash";
  const isArchiveFolder = folder === "archive";

  const selectedCount = (() => {
    if (view === "grid") {
      return table
        .getPreFilteredRowModel()
        .rows.filter((row) => !row.getIsGrouped() && row.getIsSelected())
        .length;
    }

    return Object.keys(rowSelection).length;
  })();

  const hasSelection = selectedCount > 0;

  const selectedItemOriginals = table
    .getSelectedRowModel()
    .rows.map((row) => row.original);

  const selectedItemIds = selectedItemOriginals.map(
    (item) => item.id as number,
  );

  const handleClearSelection = () => {
    table.resetRowSelection();
  };

  const handleRestoreFiles = () => {
    if (selectedItemIds.length > 0) {
      restoreFromTrashMutation.mutate(selectedItemIds);
    }
  };

  const handleDeleteFiles = () => {
    if (selectedItemIds.length > 0) {
      setSelectedItemsIds(selectedItemIds);
      toggleDeleteFilesDialog();
    }
  };

  const handleUpgrade = () => {
    setIsUpgradeModalOpen(false);
    navigate({ to: "/pricing" });
  };

  const canDownload = hasSelection;
  const showRestoreAction = isTrashFolder && hasSelection;
  const showDeleteAction = !isTrashFolder && hasSelection;
  const showUploadAction = !isTrashFolder && !hasSelection && !isArchiveFolder;

  return (
    <div className="flex items-center gap-lg">
      {hasSelection && (
        <div className="flex items-center gap-lg">
          <RACButton
            aria-label="Clear selection"
            className="flex size-[36px] items-center justify-center outline-hidden"
            onPress={handleClearSelection}
          >
            <XIcon className="text-fg-quinary" />
          </RACButton>
          <span className="text-sm font-medium text-text-tertiary">
            {selectedCount} item(s) selected
          </span>
        </div>
      )}
      <Button
        color="gray"
        isDisabled={!canDownload}
        onPress={() => {
          if (isTemplate) {
            setIsUpgradeModalOpen(true);

            return;
          }
          if (selectedItemIds.length > 0) {
            downloadBulkFiles(selectedItemIds);
            table.resetRowSelection();
          }
        }}
        variant="secondary"
      >
        <DownloadIcon />
        {isMobile ? "" : "Download"}
      </Button>
      {showRestoreAction && (
        <Button color="gray" onPress={handleRestoreFiles} variant="secondary">
          <Undo2Icon />
          Restore file(s)
        </Button>
      )}
      {showDeleteAction && (
        <FeatureGuard feature="my-files.files.delete">
          <Button
            color="destructive"
            onPress={handleDeleteFiles}
            variant="secondary"
          >
            <Trash2Icon />
            {isMobile ? "" : "Delete"}
          </Button>
        </FeatureGuard>
      )}
      {showUploadAction && <UploadAction />}

      {/* Kikstarter Upeselling Modal */}
      <UnlockModal
        isOpen={isUpgradeModalOpen}
        onOpenChange={setIsUpgradeModalOpen}
        onUpgrade={handleUpgrade}
      />
    </div>
  );
}
